import React from 'react';
import { useState, useEffect, useRef } from 'react';
import { MdMicNone, MdMic } from "react-icons/md";
import axios from 'axios';
import useAuth from '../../Users/hooks/useAuth';
import PacmanLoader from "react-spinners/PacmanLoader";
import Paginator from './Paginator';

const Slideshow = ({ transcript, title, version, language, onClose }) => {
    const [loading, setLoading] = useState(false);
    const [segment, setSegment] = useState();
    const [isgranted, setIsGranted] = useState(false);
    const [stream, setStream] = useState();
    const [audio, setAudio] = useState(null);
    const [buttonState, setButtonState] = useState(true);
    const [assessment, setAssessment] = useState();
    const { auth } = useAuth();
    const [segments, setSegments] = useState();
    const [maxpage, setMaxPage] = useState(1);
    const [page, setPage] = useState(1);
    const [recordingStatus, setRecordingStatus] = useState("inactive");

    const mediaRecorder = useRef(null);
    const [audioChunks, setAudioChunks] = useState([]);
    const recordRef = useRef(null);
    const pulseRef = useRef(null);
    const recordingTimerRef = useRef(null);

    const sendBlob = React.useCallback(({ blob, title, version, language, index, segment }) => {
        if (loading) return;
        
        const token = localStorage.getItem("token");
        const url = `${process.env.REACT_APP_USER_API_URL}/analysis/upload_blob_presentation/${language}/${title}/${version}/${index}`;
        const formData = new FormData();
        
        formData.append('uploaded_file', blob);
        formData.append('sendText', JSON.stringify({ reference: segment }));

        const config = {
            headers: {
                'content-type': blob.type, 
                Authorization: `Bearer ${token}`
            },
        };

        setLoading(true);
        
        axios.post(url, formData, config)
            .then((response) => {
                setAssessment(response.data);
            })
            .catch((error) => {
                console.error("Error uploading file: ", error);
                setAssessment(null);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [loading]);

    const pulse_frames = [
        { transform: "scale(1.2)" },
        { transform: "scale(1.0)" },
    ];

    const progress_frames = [
        { transform: "scale(1.05)", color: "inherit" },
        { transform: "translateX(160px)", color: "inherit" },
        { transform: "scale(1.0)", color: "inherit" },
    ];

    const getMicrophonePermission = async () => {
        if ("MediaRecorder" in window) {
            try {
                const streamData = await navigator.mediaDevices.getUserMedia({
                    audio: true,
                    video: false,
                });
                setIsGranted(true);
                setStream(streamData);
            } catch (err) {
                console.error("Microphone permission error:", err.message);
            }
        }
    };

    useEffect(() => {
        if (navigator.mediaDevices) {
            navigator.mediaDevices.getUserMedia({ audio: true })
                .then(streamData => {
                    setIsGranted(true);
                    setStream(streamData);
                })
                .catch(err => {
                    console.log("Microphone permission not granted:", err.message);
                });
        }
    }, []);

    useEffect(() => {
        if (transcript?.segments) {
            setSegments(transcript.segments);
            setPage(1);
            setMaxPage(transcript.segments.length);
        }
    }, [transcript, version, language, title]);

    useEffect(() => {
        if (segments && page) {
            setSegment(segments[page - 1]);
            setAssessment(null);
            setAudio(null);
        }
    }, [page, segments, version]);

    const startRecording = async () => {
        if (!stream || recordingStatus === "recording") return;

        setRecordingStatus("recording");
        setButtonState(true);
        setAssessment(null);
        
        const media = new MediaRecorder(stream, { type: "audio/webm" });
        mediaRecorder.current = media;
        mediaRecorder.current.start();
        
        let localAudioChunks = [];
        
        mediaRecorder.current.ondataavailable = (event) => {
            if (typeof event.data === "undefined" || event.data.size === 0) return;
            localAudioChunks.push(event.data);
        };
        
        setAudioChunks(localAudioChunks);

        recordingTimerRef.current = setTimeout(() => {
            stopRecording();
        }, 30000);

        if (recordRef.current) {
            recordRef.current.animate(progress_frames, { duration: 30000, fill: "forwards", iterations: 1 });
        }
        if (pulseRef.current) {
            pulseRef.current.animate(pulse_frames, { duration: 500, iterations: Infinity });
        }
    };

    const stopRecording = () => {
        if (mediaRecorder.current && recordingStatus === "recording") {
            setRecordingStatus("inactive");
            setButtonState(false);
            
            if (recordingTimerRef.current) {
                clearTimeout(recordingTimerRef.current);
            }

            if (recordRef.current) {
                recordRef.current.getAnimations().forEach(animation => animation.cancel());
            }
            if (pulseRef.current) {
                pulseRef.current.getAnimations().forEach(animation => animation.cancel());
            }

            mediaRecorder.current.stop();
            mediaRecorder.current.onstop = () => {
                const audioBlob = new Blob(audioChunks, { type: "audio/webm" });
                const audioUrl = URL.createObjectURL(audioBlob);
                
                sendBlob({
                    blob: audioBlob,
                    language: language,
                    title: title,
                    version: version,
                    segment: segment,
                    index: page
                });
                
                setAudio(audioUrl);
                setAudioChunks([]);
            };
        }
    };

    const playRecording = () => {
        if (audio) {
            const audioElement = new Audio(audio);
            audioElement.play().catch(error => {
                console.error("Error playing audio:", error);
            });
        }
    };

    return (
        <div className="w-full max-w-6xl mx-auto bg-white rounded-lg shadow-lg p-4">
            {/* Compact Header */}
            <div className="flex items-center justify-between mb-4">
                <button 
                    onClick={onClose}
                    className="flex items-center gap-2 px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Back
                </button>
                
                <div className="text-center flex-1">
                    <h2 className="text-lg font-bold text-gray-800">{title}</h2>
                    <p className="text-xs text-gray-600">V{version} • {language}</p>
                </div>
                
                <div className="w-20"></div>
            </div>

            {/* Compact Paginator */}
            <div className="mb-4">
                <Paginator 
                    maxpage={maxpage} 
                    page={page}
                    clickHandler={(page) => setPage(page)} 
                />
            </div>

            {/* Two Column Compact Layout */}
            <div className="grid lg:grid-cols-2 gap-4">
                {/* Left Column */}
                <div className="space-y-4">
                    {/* Current Segment - Compact */}
                    <div className="bg-blue-50 rounded-lg border border-blue-200 p-3">
                        <h3 className="text-sm font-semibold text-blue-700 mb-2">Current Text</h3>
                        <div className="bg-white rounded p-3 border border-blue-100">
                            <p className="text-gray-800 text-sm leading-relaxed">
                                {segment || "No segment available"}
                            </p>
                        </div>
                    </div>

                    {/* Recording Section - Compact */}
                    <div className="bg-gray-50 rounded-lg border border-gray-200 p-3">
                        <div className="flex flex-col items-center space-y-3">
                            {/* Microphone Visual */}
                            <div className="flex items-center space-x-3">
                                <div className="text-2xl text-blue-500">
                                    {recordingStatus === "inactive" ? <MdMicNone /> : <div ref={pulseRef}><MdMic /></div>}
                                </div>
                                <div className="w-32 h-2 bg-gray-200 rounded-full overflow-hidden">
                                    <div 
                                        ref={recordRef}
                                        className="h-full bg-blue-500 rounded-full transition-all duration-300"
                                    ></div>
                                </div>
                            </div>

                            {/* Controls */}
                            <div className="flex gap-2">
                                {recordingStatus === "inactive" ? (
                                    <button 
                                        className="px-4 py-2 bg-green-500 text-white text-sm rounded hover:bg-green-600 transition-colors disabled:opacity-50"
                                        onClick={startRecording}
                                        disabled={loading || !isgranted}
                                    >
                                        Record
                                    </button>
                                ) : (
                                    <button 
                                        className="px-4 py-2 bg-red-500 text-white text-sm rounded hover:bg-red-600 transition-colors"
                                        onClick={stopRecording}
                                    >
                                        Stop
                                    </button>
                                )}
                                
                                <button 
                                    className={`px-4 py-2 text-sm rounded transition-colors ${
                                        !audio || buttonState 
                                            ? "bg-gray-300 text-gray-500 cursor-not-allowed" 
                                            : "bg-blue-500 text-white hover:bg-blue-600"
                                    }`}
                                    onClick={playRecording}
                                    disabled={!audio || buttonState}
                                >
                                    Play
                                </button>
                            </div>

                            {!isgranted && (
                                <button 
                                    className="px-3 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600 transition-colors"
                                    onClick={getMicrophonePermission}
                                >
                                    Enable Mic
                                </button>
                            )}

                            {recordingStatus === "recording" && (
                                <p className="text-xs text-gray-600 animate-pulse">
                                    Recording... (30s)
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Right Column - Assessment */}
                <div className="space-y-4">
                    {/* Guest Notice */}
                    {auth.username === 'guest' && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                            <p className="text-yellow-700 text-xs text-center">
                                AI assessment available for registered users
                            </p>
                        </div>
                    )}

                    {/* Loading */}
                    {loading && (
                        <div className="flex flex-col items-center justify-center p-4 bg-gray-50 rounded-lg border">
                            <PacmanLoader loading={loading} color="#3B82F6" size={15} />
                            <span className="ml-2 text-gray-600 text-sm">Analyzing...</span>
                        </div>
                    )}

                    {/* Assessment Results */}
                    {assessment && auth.username !== 'guest' && (
                        <div className="space-y-4 max-h-96 overflow-y-auto">
                            {/* User Assessment */}
                            {assessment.user?.NBest && (
                                <div className="bg-green-50 rounded-lg border border-green-200 p-3">
                                    <h4 className="text-sm font-semibold text-green-700 mb-2 text-center">Your Score</h4>
                                    <div className="space-y-3">
                                        <div className="flex flex-wrap gap-1">
                                            {assessment.user.NBest[0].Words.map((word, index) => (
                                                word.PronunciationAssessment.ErrorType !== 'Omission' ? (
                                                    <div 
                                                        key={index}
                                                        className={`flex flex-col items-center p-1 rounded border text-xs ${
                                                            word.PronunciationAssessment.AccuracyScore >= 80 
                                                                ? "bg-green-100 border-green-300" 
                                                                : word.PronunciationAssessment.AccuracyScore >= 40 
                                                                ? "bg-yellow-100 border-yellow-300" 
                                                                : "bg-red-100 border-red-300"
                                                        }`}
                                                    >
                                                        <span className="font-semibold">{word.Word}</span>
                                                        <span className="text-blue-600">
                                                            {word.PronunciationAssessment.AccuracyScore}%
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <div key={index} className="w-4 h-4 bg-gray-400 rounded" title="Omitted" />
                                                )
                                            ))}
                                        </div>
                                        
                                        <div className="bg-white rounded p-2 border border-green-300">
                                            <h5 className="font-semibold text-green-700 text-xs mb-2">Overall</h5>
                                            {Object.entries(assessment.user.NBest[0].PronunciationAssessment).map(([key, value]) => (
                                                <div key={key} className="flex justify-between text-xs py-0.5">
                                                    <span className="text-gray-600 capitalize">{key}:</span>
                                                    <span className="font-medium text-green-700">{value}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Narrator Assessment */}
                            {assessment.narrator?.NBest && (
                                <div className="bg-blue-50 rounded-lg border border-blue-200 p-3">
                                    <h4 className="text-sm font-semibold text-blue-700 mb-2 text-center">Reference</h4>
                                    <div className="space-y-3">
                                        <div className="flex flex-wrap gap-1">
                                            {assessment.narrator.NBest[0].Words.map((word, index) => (
                                                <div key={index} className="flex flex-col items-center p-1 bg-white rounded border border-blue-200 text-xs">
                                                    <span className="font-semibold">{word.Word}</span>
                                                    <span className="text-blue-600">{word.PronunciationAssessment.AccuracyScore}%</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Slideshow;